import * as React from 'react';
import { st, classes } from './LinearProgressBarCore.st.css';
import {
  ProgressBarDataHooks,
  ProgressBarDataKeys,
  ProgressBarAriaKeys,
} from './DataHooks';
import { filterDataProps } from './utils/filter-data-props';

export interface LinearProgressBarProps {
  /** represent the progress state in percentages (min || 0 - no progress, max || 100 - progress completed) */
  value?: number | string;
  /** should be true if had failure during the progress */
  error?: boolean;
  /** when set to true, an indication of the progress state will be presented along side the progress bar */
  showProgressIndication?: boolean;
  /** an indication icon (any react component) that will be presented when 'error' and 'showProgressIndication' are set to true */
  errorIcon?: React.ReactElement;
  /** an indication icon (any react component) that will be presented when 'showProgressIndication' are set to true and 'value' is equal or bigger than 'max' */
  successIcon?: React.ReactElement;
  /** minimum value for progress bar, default value: 0 */
  min?: number;
  /** maximum value for progress bar, default value: 100 */
  max?: number;
  /** Number of decimal points to keep when normalizing value */
  precision?: number;
  /** A prefix node for the progress bar */
  prefixIndication?: React.ReactNode;
  /** A custom text for the progress bar indication. */
  customSuffixIndicationText?: string;
  /** onClick on the component */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** aria-label - Accessibility */
  'aria-label'?: string;
  /** Applied as data-hook HTML attribute that can be used to create driver in testing */
  'data-hook'?: string;
  /** add aria-hidden to prefix/suffix */
  'aria-hide-affixes'?: boolean;
  role?: string;
  className?: string;
}

export interface LinearProgressBarPropsWithAria extends LinearProgressBarProps {
  [ProgressBarAriaKeys.valuetext]?: string;
}

const FULL_PROGRESS = 100;
const NO_PROGRESS = 0;

const resolveIndicationElement = (props: LinearProgressBarProps) => {
  const wrapped = (dataHook: string, children: React.ReactElement | string) => (
    <div data-hook={dataHook} className={classes.indicationContainer}>
      {children}
    </div>
  );

  if (props.error && props.errorIcon) {
    return wrapped(
      ProgressBarDataHooks.errorIcon,
      <span className={classes.progressIcon}>{props.errorIcon}</span>,
    );
  }

  if (props.value === FULL_PROGRESS && props.successIcon) {
    return wrapped(
      ProgressBarDataHooks.successIcon,
      <span className={classes.progressIcon}>{props.successIcon}</span>,
    );
  }

  if (props.customSuffixIndicationText) {
    return wrapped(
      ProgressBarDataHooks.suffixIndicator,
      <span className={classes.suffixIndication}>
        {props.customSuffixIndicationText}
      </span>,
    );
  }

  return wrapped(
    ProgressBarDataHooks.progressPercentage,
    <span className={classes.progressPercentage}>{`${props.value}%`}</span>,
  );
};

const renderBarSection = (
  value: number | string,
  isButton: boolean = false,
  ariaLabel?: string,
) => {
  const progressWidth = { width: `${value}%` };
  const Component = isButton ? 'button' : 'div';
  return (
    <Component
      data-hook={ProgressBarDataHooks.container}
      className={classes.barContainer}
      aria-label={ariaLabel}
    >
      <div
        data-hook={ProgressBarDataHooks.background}
        className={classes.barBackground}
      />
      <div
        data-hook={ProgressBarDataHooks.foreground}
        style={progressWidth}
        className={classes.barForeground}
      />
    </Component>
  );
};

const getRelativeValue = (props: LinearProgressBarProps): number => {
  const { value = 0, min = 0, max = 100, precision } = props;

  const relativeValue = +(((Number(value) - min) / (max - min)) * 100).toFixed(
    precision || 1,
  );

  return precision ? relativeValue : parseInt(relativeValue as any, 10);
};

const normalizeProps = (props: LinearProgressBarProps) => {
  const { value, min, max } = props;

  if (value !== undefined && max !== undefined && +value >= max) {
    return { ...props, value: FULL_PROGRESS };
  }

  if (
    [undefined, null, ''].includes(value as string) ||
    (value !== undefined && min !== undefined && +value < min)
  ) {
    return { ...props, value: NO_PROGRESS };
  }

  return { ...props, value: getRelativeValue(props) };
};

const getDataAttributes = (
  props: LinearProgressBarProps,
): { [key in ProgressBarDataKeys]: number | string | undefined } => {
  const { value, min, max } = props;
  return {
    [ProgressBarDataKeys.value]: value,
    [ProgressBarDataKeys.min]: min,
    [ProgressBarDataKeys.max]: max,
  };
};

const getAriaAttributes = (
  props: LinearProgressBarPropsWithAria,
): {
  [key in ProgressBarAriaKeys]: React.HTMLAttributes<HTMLDivElement>[key];
} => {
  return {
    [ProgressBarAriaKeys.valuenow]:
      props.value !== undefined ? +props.value : NO_PROGRESS,
    [ProgressBarAriaKeys.valuemin]:
      props.min !== undefined ? +props.min : props.min,
    [ProgressBarAriaKeys.valuemax]:
      props.max !== undefined ? +props.max : props.max,
    [ProgressBarAriaKeys.valuetext]: props[ProgressBarAriaKeys.valuetext],
  };
};

const LinearProgressBarCore: React.FunctionComponent<
  LinearProgressBarPropsWithAria
> = ({
  min = NO_PROGRESS,
  max = FULL_PROGRESS,
  role = 'progressbar',
  ...propsWithNoDefaults
}: LinearProgressBarPropsWithAria) => {
  const {
    error,
    showProgressIndication,
    prefixIndication,
    className,
    onClick,
  } = propsWithNoDefaults;
  const allProps = {
    min,
    max,
    role,
    ...propsWithNoDefaults,
  };
  const _props = normalizeProps({
    min,
    max,
    role,
    ...propsWithNoDefaults,
  });
  const success = _props.value === FULL_PROGRESS;
  return (
    <div
      {...getDataAttributes(_props)}
      {...(role === 'progressbar' ? getAriaAttributes(allProps) : {})}
      data-min={_props.min}
      data-error={error}
      {...(role ? { role } : {})}
      className={st(classes.root, { error, success }, className)}
      {...filterDataProps(allProps)}
      onClick={onClick}
    >
      {prefixIndication && (
        <div
          data-hook={ProgressBarDataHooks.prefixIndicator}
          className={classes.prefixIndicationContainer}
          aria-hidden={allProps['aria-hide-affixes']}
        >
          {prefixIndication}
        </div>
      )}
      {renderBarSection(_props.value, !!onClick, allProps['aria-label'])}

      {showProgressIndication && (
        <div
          data-hook={ProgressBarDataHooks.progressIndicator}
          className={classes.progressIndicationSection}
          aria-hidden={allProps['aria-hide-affixes']}
        >
          {resolveIndicationElement(_props)}
        </div>
      )}
    </div>
  );
};

LinearProgressBarCore.displayName = 'LinearProgressBar';

export default LinearProgressBarCore;
